// src/utils/livePermissions.ts
//
// Kdo smí co v živém bodování – jedno místo, které zrcadlí firestore.rules
// (docs/live-faze1-navrh.md bod 2, docs/live-faze2-navrh.md bod 2.1).
// Fáze 2 ho používá v nastavení závodu; obrazovka rozhodčího (fáze 3),
// Živé výsledky a tabule (fáze 4) ho převezmou místo vlastních kopií
// (dnes se liší: `headJudgeUids ?? [headJudgeUid]` místo OR obou polí,
// isSuperAdmin místo správce, rozhodčí podle stanovišť místo liveJudgeUids).
//
// UI podle něj jen skrývá a zamyká ovládání – rozhoduje vždy server.

/** Závod tak, jak ho čtou pravidla (pole races/{id}). */
export interface LiveRaceLike {
  status?: unknown;
  liveScoring?: unknown;
  liveStartedAt?: unknown;
  finishedAt?: unknown;
  headJudgeUids?: unknown;
  headJudgeUid?: unknown;
  liveJudgeUids?: unknown;
}

/** Role z users/{uid}.role ('admin', 'superadmin', 'user', …). */
export type UserRole = string | null | undefined;

/**
 * Kdo se dívá: uid a správce / superadmin tak, jak je počítá useAuth –
 * superadmin je i trvalý e-mail majitele (pravidla isSuperAdmin), proto
 * nestačí role z profilu. Správce = admin nebo superadmin (pravidla isStaff).
 */
export interface LiveViewer {
  uid: string | null | undefined;
  isStaff: boolean;
  isSuperAdmin?: boolean;
}

/** Diváka z useAuth: { user, role, isSuperAdmin }. */
export function liveViewer(auth: { user?: { uid: string } | null; role?: UserRole; isSuperAdmin?: boolean }): LiveViewer {
  const isSuperAdmin = !!auth.isSuperAdmin;
  return { uid: auth.user ? auth.user.uid : null, isStaff: isStaffRole(auth.role) || isSuperAdmin, isSuperAdmin };
}

/** Rozstřel smí rozhodčí stanovišť zapsat ještě 48 h po ukončení (pravidla judgeTiebreakerWrite). */
export const TIEBREAKER_WINDOW_MS = 48 * 60 * 60 * 1000;

/** Správce = admin nebo superadmin (pravidla isStaff). */
export function isStaffRole(role: UserRole): boolean {
  return role === "admin" || role === "superadmin";
}

/** Hlavní rozhodčí závodu: pole headJudgeUids NEBO starý headJudgeUid (jako pravidla isHeadJudge). */
export function isHeadJudgeOf(race: LiveRaceLike | null | undefined, uid: string | null | undefined): boolean {
  if (!race || !uid) return false;
  return (Array.isArray(race.headJudgeUids) && race.headJudgeUids.includes(uid)) || race.headJudgeUid === uid;
}

/** Rozhodčí stanovišť závodu (races.liveJudgeUids – pravidla isLiveJudge). */
export function isLiveJudgeOf(race: LiveRaceLike | null | undefined, uid: string | null | undefined): boolean {
  if (!race || !uid) return false;
  return Array.isArray(race.liveJudgeUids) && race.liveJudgeUids.includes(uid);
}

/** Fáze živého závodu podle stavu: běží, ukončený, jinak příprava (i bez pole status). */
export type LivePhase = "setup" | "active" | "finished";

export function raceLivePhase(race: LiveRaceLike | null | undefined): LivePhase {
  if (!race) return "setup";
  if (race.status === "finished") return "finished";
  if (race.status === "active") return "active";
  return "setup";
}

/**
 * Závod už byl spuštěný (běží, skončil, nebo má čas spuštění – např. stav
 * vrácený formulářem závodu): od té chvíle se startovní čísla nepřečíslují
 * (docs/live-faze2-navrh.md bod 2.2).
 */
export function wasLaunched(race: LiveRaceLike | null | undefined): boolean {
  if (!race) return false;
  return race.status === "active" || race.status === "finished" || (race.liveStartedAt !== undefined && race.liveStartedAt !== null);
}

/** Čas z Firestore Timestamp / Date / čísla (ms); jinak null. */
export function timeMillis(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.getTime() : null;
  const v = value as { toMillis?: () => number; seconds?: unknown };
  if (typeof v.toMillis === "function") {
    const ms = v.toMillis();
    return Number.isFinite(ms) ? ms : null;
  }
  if (typeof v.seconds === "number") return v.seconds * 1000;
  return null;
}

/** Práva na stránce nastavení závodu (krok 11). */
export interface SetupPermissions {
  /** Stránku smí otevřít (správce nebo hlavní rozhodčí závodu). */
  canView: boolean;
  /** Stanoviště, rundy, rotace (jen správce, ne ukončený závod). */
  canEditSetup: boolean;
  /** Rozhodčí stanovišť (správce celý zápis, hlavní rozhodčí úzký zápis), ne ukončený závod. */
  canEditStationJudges: boolean;
  /** Hlavní rozhodčí závodu (jen správce, ne ukončený závod). */
  canEditHeadJudges: boolean;
  /**
   * Soupiska – střelci v rundách (jen správce; i po ukončení – opravy před
   * znovuzveřejněním, rozhodnutí majitele 2 k fázi 2).
   */
  canEditRoster: boolean;
  /** Nováčci – i po ukončení (jinde se nováček po ukončení opravit nedá). */
  canEditNewbies: boolean;
  /** Spuštění (správce, závod ještě neběží ani neskončil). */
  canLaunch: boolean;
  /** Vrácení spuštěného závodu do přípravy (správce, závod běží; bez výsledků ověří zápis). */
  canRevert: boolean;
  /** Veřejné živé výsledky (livePublic) – správce i hlavní rozhodčí, jen běžící závod. */
  canToggleLivePublic: boolean;
  /** Uživatel je hlavní rozhodčí závodu bez role správce. */
  isHeadJudgeOnly: boolean;
}

export function setupPermissions(race: LiveRaceLike | null | undefined, viewer: LiveViewer): SetupPermissions {
  const uid = viewer.uid;
  const staff = !!uid && viewer.isStaff;
  const headJudge = isHeadJudgeOf(race, uid);
  const phase = raceLivePhase(race);
  const notFinished = !!race && phase !== "finished";
  return {
    canView: !!race && !!uid && (staff || headJudge),
    canEditSetup: staff && notFinished,
    canEditStationJudges: (staff || headJudge) && notFinished,
    canEditHeadJudges: staff && notFinished,
    canEditRoster: staff && !!race,
    canEditNewbies: !!race && (staff || headJudge),
    canLaunch: staff && !!race && phase === "setup",
    canRevert: staff && phase === "active",
    canToggleLivePublic: (staff || headJudge) && phase === "active",
    isHeadJudgeOnly: headJudge && !staff,
  };
}

/**
 * „Trvale zakázat nováčka“ zapisuje users/{uid}.newbieDisabled: superadmin
 * (i trvalý e-mail) každý profil, admin jen profil střelce (role prázdná
 * nebo user) – pravidla users update. Neznámá role cílového profilu = jen
 * superadmin.
 */
export function canDisableNewbie(viewer: LiveViewer, targetRole: UserRole): boolean {
  if (!viewer.uid) return false;
  if (viewer.isSuperAdmin) return true;
  if (!viewer.isStaff) return false;
  return targetRole === "" || targetRole === "user";
}

/**
 * Zápis výsledku stanoviště: správce a hlavní rozhodčí kdykoli (i před
 * spuštěním a po ukončení), rozhodčí stanovišť jen za běhu.
 */
export function canWriteStationResult(race: LiveRaceLike | null | undefined, viewer: LiveViewer): boolean {
  const uid = viewer.uid;
  if (!race || !uid) return false;
  if (viewer.isStaff || isHeadJudgeOf(race, uid)) return true;
  return isLiveJudgeOf(race, uid) && raceLivePhase(race) === "active";
}

/**
 * Rozstřel: správce a hlavní rozhodčí kdykoli, rozhodčí stanovišť za běhu
 * a do 48 h po ukončení (finishedAt).
 */
export function canWriteTiebreaker(race: LiveRaceLike | null | undefined, viewer: LiveViewer, nowMs: number): boolean {
  const uid = viewer.uid;
  if (!race || !uid) return false;
  if (viewer.isStaff || isHeadJudgeOf(race, uid)) return true;
  if (!isLiveJudgeOf(race, uid)) return false;
  const phase = raceLivePhase(race);
  if (phase === "active") return true;
  if (phase !== "finished") return false;
  const finished = timeMillis(race.finishedAt);
  return finished !== null && nowMs < finished + TIEBREAKER_WINDOW_MS;
}

/** Ukončení závodu: správce nebo hlavní rozhodčí, jen běžící závod. */
export function canFinishRace(race: LiveRaceLike | null | undefined, viewer: LiveViewer): boolean {
  if (!race || !viewer.uid) return false;
  return (viewer.isStaff || isHeadJudgeOf(race, viewer.uid)) && raceLivePhase(race) === "active";
}

/** Zveřejnění výsledků (results_* a pole publikace na závodě): správce nebo hlavní rozhodčí. */
export function canPublishResults(race: LiveRaceLike | null | undefined, viewer: LiveViewer): boolean {
  if (!race || !viewer.uid) return false;
  return viewer.isStaff || isHeadJudgeOf(race, viewer.uid);
}
